<template>
  <div
    class="fts-client fts-client--refined"
    :data-workspace-rtc-status="workspaceRTCStatus"
    :data-user-rtc-status="userRTCStatus"
    ref="client"
    v-if="prefReady"
  >
    <template v-if="contentReady">
      <!-- sidenav -->
      <div
        class="client-sidenav"
        @mouseenter="sidebarMouseEvent"
        @mouseleave="sidebarMouseEvent"
        ref="sidenav"
        v-if="!settingsOpen"
      >
        <div class="client-sidenav-header">
          <div class="client-sidenav-wrapper">
            <img :src="sidenavLogo" class="client-sidenav-logo" />
          </div>
        </div>
        <div class="client-sidenav-body fts-scrollbar fts-scrollbar-overlay">
          <fts-tippy
            triggerClass="sidenav-menu-outer"
            :content="workspace.name"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
            v-for="workspace in preWorkspaces"
            :key="workspace.id"
          >
            <div
              slot="trigger"
              class="sidenav-menu-wrapper"
              :class="{ active: workspaceId == workspace.id }"
              @click="selectWorkspace(workspace)"
            >
              <div class="sidenav-workspace">
                {{workspace.shortName}}
              </div>
            </div>
          </fts-tippy>

          <template v-if="!workspaceLobby">
            <!-- notification -->
            <fts-dropdown-select
              append-to-body
              placement="right-start"
              prevent-overflow-alt-axis
              prevent-overflow-tether-off
              :offset="[0, -3]"
              content-class="notification-content notification-dropdown-menu notification-dropdown-menu--refined"
              class="notif-dropdown"
              @show="openNotification"
              @selected="openQuickView($event)"
            >
              <fts-tippy
                slot="toggle"
                content="Notifications"
                :options="{ placement: 'left' }"
                trigger-class="sidenav-menu-wrapper"
                class="sidenav-menu"
              >
                <div class="notif-dropdown-toggle">
                  <font-awesome-icon
                    :icon="['far', 'bell']"
                    fixed-width
                    size="lg"
                    class="sidenav-icon"
                  />
                  <div class="notif-count" v-if="unreadCount > 0">
                    {{ formatBadgeCount(unreadCount) }}
                  </div>
                </div>
              </fts-tippy>
              <template #menu="{select}">
                <!-- notification:header -->
                <div class="notification-dropdown__header">
                  <div class="notification-header-title">Notifications</div>
                </div>
                <!-- notification:content -->
                <div class="notification-dropdown__content fts-scrollbar">
                  <template v-if="notifications && notifications.length">
                    <fts-notification-content
                      v-for="notification in notifications"
                      :key="notification.id"
                      :notification="notification"
                      @select="select"
                    />
                  </template>
                  <!-- infinite loading -->
                  <infinite-loading v-if="notifReady" @infinite="infiniteHandler">
                    <fts-spinner
                      show
                      :size="24"
                      slot="spinner"
                      class="notification-loader-spinner"
                    />
                    <div slot="no-more" class="notification-loader-blank">
                      No older notifications
                    </div>
                    <div slot="no-results" class="notification-loader-blank">
                      No older notifications
                    </div>
                  </infinite-loading>
                </div>
              </template>
            </fts-dropdown-select>
            <!-- dashboard -->
            <fts-tippy
              triggerClass="sidenav-menu-outer"
              content="Dashboard"
              :options="{ placement: 'left' }"
              class="sidenav-menu"
            >
              <router-link
                slot="trigger"
                to="/dashboard"
                custom
                class="sidenav-menu-wrapper"
                data-v-step="tour-dashboard"
              >
                <font-awesome-icon
                  :icon="['fas', 'chart-column']"
                  fixed-width
                  size="lg"
                  class="sidenav-icon"
                />
              </router-link>
            </fts-tippy>
            <!-- calendar -->
            <fts-tippy
              triggerClass="sidenav-menu-outer"
              content="My Calendar"
              :options="{ placement: 'left' }"
              class="sidenav-menu"
            >
              <router-link
                slot="trigger"
                to="/calendar"
                custom
                class="sidenav-menu-wrapper"
                data-v-step="tour-calendar"
              >
                <font-awesome-icon
                  :icon="['far', 'calendar']"
                  fixed-width
                  size="lg"
                  class="sidenav-icon"
                />
              </router-link>
            </fts-tippy>
            <!-- integrations (alpha) — Routines, Connections and Apps share one
                 rail entry; every child keeps its own availability gate and the
                 group disappears when none of them qualify (see integrationsMenu) -->
            <fts-dropdown-select
              append-to-body
              placement="right-start"
              prevent-overflow-alt-axis
              prevent-overflow-tether-off
              :offset="[0, -3]"
              content-class="integrations-content integrations-dropdown-menu"
              class="integrations-dropdown"
              v-if="integrationsMenu.length"
            >
              <fts-tippy
                slot="toggle"
                content="Integrations (alpha)"
                :options="{ placement: 'left' }"
                :trigger-class="integrationsActive ? 'sidenav-menu-wrapper active' : 'sidenav-menu-wrapper'"
                class="sidenav-menu"
              >
                <font-awesome-icon
                  :icon="['fas', 'puzzle-piece']"
                  fixed-width
                  size="lg"
                  class="sidenav-icon"
                />
              </fts-tippy>
              <template #menu="{select}">
                <!-- integrations:header -->
                <div class="integrations-dropdown__header">
                  <div class="integrations-header-title">Integrations</div>
                  <fts-release-badge alpha />
                </div>
                <!-- integrations:content -->
                <div class="integrations-dropdown__content">
                  <router-link
                    v-for="item in integrationsMenu"
                    :key="item.route"
                    :to="{ name: item.route }"
                    class="integrations-option"
                    @click.native="select(item)"
                  >
                    <font-awesome-icon
                      :icon="['fas', item.icon]"
                      fixed-width
                      class="integrations-option-icon"
                    />
                    <div class="integrations-option-body">
                      <div class="integrations-option-title">{{ item.title }}</div>
                      <div class="integrations-option-description">
                        {{ item.description }}
                      </div>
                    </div>
                  </router-link>
                </div>
              </template>
            </fts-dropdown-select>
            <!-- users -->
            <fts-tippy
              triggerClass="sidenav-menu-outer"
              content="Users"
              :options="{ placement: 'left' }"
              class="sidenav-menu"
              v-if="isAdminUser || isManagerUser"
            >
              <router-link
                slot="trigger"
                :to="usersRoute"
                class="sidenav-menu-wrapper"
                data-v-step="tour-manage-user"
              >
                <font-awesome-icon
                  :icon="['fas', 'user-friends']"
                  fixed-width
                  size="lg"
                  class="sidenav-icon"
                />
              </router-link>
            </fts-tippy>
            <!-- invite -->
            <fts-tippy
              triggerClass="sidenav-menu-outer"
              content="Invite"
              :options="{ placement: 'left' }"
              class="sidenav-menu"
              v-if="isAdminUser || isManagerUser"
            >
              <div
                slot="trigger"
                class="sidenav-menu-wrapper"
                @click="openInvitation"
                data-v-step="tour-invite-user"
              >
                <font-awesome-icon
                  :icon="['fas', 'user-plus']"
                  fixed-width
                  size="lg"
                  class="sidenav-icon"
                />
              </div>
            </fts-tippy>
          </template>
        </div>
        <!-- sidenav footer -->
        <div class="client-sidenav-footer">
          <template v-if="!workspaceLobby">
               <!-- invite -->
            <fts-tippy
              triggerClass="sidenav-menu-outer"
              :content="enabledGneyAi ? 'gney, take a nap' : 'gney, wake up'"
              :options="{ placement: 'left' }"
              class="sidenav-menu"
            >
              <div
                slot="trigger"
                class="sidenav-menu-wrapper"
                @click="toggleGneyAiEnable"
                data-v-step="tour-invite-user"
              >
                <i
                  class="fts-icon-magic-lamp sidenav-icon"
                  :style="{ opacity: enabledGneyAi ? 1 : 0.4, fontSize: '1.25rem' }"
                ></i>
              </div>
            </fts-tippy>

            <!-- preview -->
            <fts-tippy
              triggerClass="sidenav-menu-outer"
              content="Device preview"
              :options="{ placement: 'left' }"
              class="sidenav-menu apex"
              v-if="showDevicePreview"
            >
              <router-link
                slot="trigger"
                :to="{ name: 'device-preview' }"
                target="_blank"
                class="sidenav-menu-wrapper"
              >
                <font-awesome-icon
                  :icon="['fas', 'mobile-screen-button']"
                  fixed-width
                  size="lg"
                  class="sidenav-icon"
                />
              </router-link>
            </fts-tippy>
            <!-- studio -->
            <fts-tippy
              triggerClass="sidenav-menu-outer"
              content="Design"
              :options="{ placement: 'left' }"
              class="sidenav-menu apex"
              v-if="isAdminUser && !builderMode"
            >
              <router-link
                slot="trigger"
                :to="designRoute"
                class="sidenav-menu-wrapper"
                data-v-step="tour-studio"
              >
                <font-awesome-icon
                  :icon="['fas', 'wand-magic-sparkles']"
                  fixed-width
                  size="lg"
                  class="sidenav-icon"
                />
              </router-link>
            </fts-tippy>
          </template>
          <!-- demo mode (internal only) -->
          <fts-tippy
            triggerClass="sidenav-menu-outer"
            :content="demoMode ? 'Demo mode ON — click to restore alpha/beta features' : 'Demo mode — hide all alpha/beta features'"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
            v-if="isInternalIdentity"
          >
            <div
              slot="trigger"
              class="sidenav-menu-wrapper demo-mode-toggle"
              :class="{ 'demo-mode-toggle--active': demoMode }"
              @click="toggleDemoMode"
            >
              <font-awesome-icon
                :icon="['far', demoMode ? 'eye-slash' : 'eye']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </div>
          </fts-tippy>
          <!-- profile -->
          <fts-profile
            :profile="profile"
            :profile-photo-size="30"
            :profile-name="false"
            profile-photo-class="user-profile-avatar"
            class="client-user-profile"
            @click.native="openUserProfileModal"
          />
        </div>
      </div>
      <!-- drawer -->
      <div
        v-if="!settingsOpen"
        class="client-drawer"
        :class="[
          {'collapsed': !isDrawerStatic },
          {'close': !isDrawerStatic && !isDrawerVisible }
        ]"
        @mouseenter="sidebarMouseEvent"
        @mouseleave="sidebarMouseEvent"
        ref="drawer"
      >
        <div class="client-drawer-header">
          <!-- workspace -->
          <fts-dropdown-select
            class="client-dropdown"
            append-toggle-class="client-dropdown-toggle"
            content-class="client-content client-dropdown-menu"
            :offset="[0, 6]"
            @show="showWorkspaceOptions"
            @hide="workspaceOptionList = []"
            @selected="selectWorkspaceOption"
          >
            <!-- toggle -->
            <template #toggle>
              <div class="workspace-toggle">
                <div class="workspace-toggle__label">
                  {{workspace.name || "Workspace"}}
                </div>
                <font-awesome-icon
                  :icon="['fas', 'caret-down']"
                  class="workspace-toggle__caret"
                />
              </div>

              <fts-tippy
                content="Search modules"
                trigger-class="client-drawer-search-toggle"
                stop-propagation
                class="client-drawer-search-toggle-wrapper"
                :class="{ 'client-drawer-search-toggle-wrapper--active': drawerSearchOpen }"
                @trigger:click="toggleDrawerSearch"
                v-if="!workspaceLobby"
              >
                <template #trigger>
                  <font-awesome-icon :icon="['fas', 'search']" />
                </template>
              </fts-tippy>

              <fts-tippy
                :content="isDrawerStatic ? 'Close sidebar' : 'Lock sidebar open'"
                trigger-class="client-drawer-toggle"
                stop-propagation
                class="client-drawer-toggle-wrapper"
                @trigger:click="toggleDrawerMode"
              >
                <template #trigger>
                  <font-awesome-icon :icon="['fas', 'angle-double-left']" v-if="isDrawerStatic" />
                  <font-awesome-icon :icon="['fas', 'thumbtack']" v-else />
                </template>
              </fts-tippy>
            </template>
            <!-- menu -->
            <template #menu="{ select, defaultOptionClass}">
              <div class="client-dropdown-item">
                <div class="client-dropdown-search">
                  <div class="fts-form-control-filter">
                    <input
                      type="text"
                      class="fts-form-control"
                      v-model.trim="workspaceSearch"
                      placeholder="Search workspace"
                      ref="workspaceSearch"
                    />
                    <div class="search-icon">
                      <fts-spinner :size="14" v-if="workspacesSearching" />
                      <font-awesome-icon :icon="['fas', 'search']" fixed-width v-else />
                    </div>
                  </div>
                </div>

                <template v-if="workspaceOptionList && workspaceOptionList.length">
                  <div
                    v-for="workspace in workspaceOptionList"
                    :key="workspace.id"
                    :class="defaultOptionClass"
                    @click="select(workspace)"
                  >
                    <div>{{workspace.name}}</div>
                    <fts-tippy
                      :content="workspace.description"
                      class="client-dropdown-option-description"
                      v-if="workspace.description"
                    >
                      {{workspace.description}}
                    </fts-tippy>
                  </div>
                </template>

                <fts-spinner height="84px" :size="24" v-else-if="workspacesLoading" />

                <div class="client-dropdown-no-found" v-else>
                  <div class="client-dropdown-no-found__icon">
                    <font-awesome-icon :icon="['fas', 'search']" />
                  </div>
                  <div class="client-dropdown-no-found__label">
                    No matching results
                  </div>
                </div>
              </div>

              <div class="client-dropdown-item" v-if="isMobileView()">
                <div
                  class="dropdown-option client-dropdown-label"
                  @click="select('requestMobileView')"
                >
                  <div class="dropdown-option-icon">
                    <font-awesome-icon :icon="['fas', 'mobile-screen-button']" fixed-width />
                  </div>
                  <div>Request mobile view&nbsp;<fts-release-badge alpha class="fts-align-top" /></div>
                </div>
              </div>

              <div class="client-dropdown-item" v-if="!builderMode && isAdminUser">
                <div
                  class="dropdown-option dropdown-option--secondary client-dropdown-label"
                  @click="select('runAs')"
                >
                  <div class="dropdown-option-icon">
                    <font-awesome-icon :icon="['far', 'user']" fixed-width />
                  </div>
                  <div>
                    Run as
                  </div>
                </div>

                <div
                  class="dropdown-option dropdown-option--secondary client-dropdown-label"
                  @click="select('edit')"
                  v-if="isAdminUser && manageWorkspaceInMenu"
                >
                  <div class="dropdown-option-icon">
                    <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
                  </div>
                  <div>Manage workspace</div>
                </div>

                <!-- A vendor company's people set up workspaces for their customers
                     (docs/billing-vendor-lead-onboarding-proposal.md N1, in the SVN repo). -->
                <div
                  :class="defaultOptionClass"
                  class="client-dropdown-label"
                  data-e2e="workspace-create-for-customer"
                  @click="select('createForCustomer')"
                  v-if="!builderMode && vendor"
                >
                  <font-awesome-icon
                    :icon="['far', 'building']"
                    fixed-width
                    class="client-dropdown-label__icon"
                  />
                  Set up a workspace for a customer
                </div>

                <div
                  :class="defaultOptionClass"
                  class="client-dropdown-label"
                  @click="select('create')"
                  v-if="!builderMode"
                >
                  <font-awesome-icon
                    :icon="['fas', 'plus']"
                    fixed-width
                    class="client-dropdown-label__icon"
                  />
                  Create a new workspace
                </div>
              </div>
            </template>
          </fts-dropdown-select>
        </div>
        <div class="client-drawer-body fts-scrollbar fts-scrollbar-overlay">
          <!-- context menu -->
          <fts-dropdown-select
            class="workspace-context-dropdown"
            append-toggle-class="workspace-context-toggle"
            content-class="client-content client-dropdown-menu"
            :offset="[0, 6]"
            @show="showContextOptions"
            @hide="contextOptionList = []"
            @selected="selectWorkspaceContext"
            v-if="workspace.hasContext">
            <template #toggle>
              <div class="workspace-context-name">
                {{ workspaceContext.name || "Select Context" }}
              </div>
              <font-awesome-icon
                :icon="['fas', 'angle-down']"
                class="workspace-context-toggle-icon"
                fixed-width
              />
            </template>
            <template #menu="{ select, defaultOptionClass}">
              <div class="client-dropdown-item">
                <div class="client-dropdown-search">
                  <div class="fts-form-control-filter">
                    <input
                      type="text"
                      class="fts-form-control"
                      v-model="workspaceContextSearch"
                      placeholder="Search context"
                      ref="workspaceContextSearch"
                    />
                    <div class="search-icon">
                      <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                    </div>
                  </div>
                </div>
                <template v-if="contextOptionList && contextOptionList.length">
                  <div
                    v-for="context in contextOptionList"
                    :key="context.id"
                    :class="defaultOptionClass"
                    @click="select(context)"
                  >
                    {{ context.name }}
                  </div>
                </template>
                <div class="client-dropdown-no-found" v-else>
                  <div class="client-dropdown-no-found__icon">
                    <font-awesome-icon :icon="['fas', 'search']" />
                  </div>
                  <div class="client-dropdown-no-found__label">
                    No matching results
                  </div>
                </div>
              </div>
            </template>
          </fts-dropdown-select>
          <template v-if="!workspaceLobby">
            <!-- portals: above the modules (PL-11), each opens in a new tab; nothing when there is none -->
            <fts-portal-nav :workspace="workspace" variant="client" />
            <!-- search -->
            <transition name="drawer-search">
              <div class="fts-form-control-filter drawer-search" v-show="drawerSearchOpen">
                <input
                  type="text"
                  class="fts-form-control"
                  placeholder="Search modules"
                  v-model.trim="drawerSearchKeyword"
                  ref="drawerSearchInput"
                  @keydown.esc="closeDrawerSearch(true)"
                  @blur="closeDrawerSearch()"
                />
                <div class="search-icon">
                  <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                </div>
              </div>
            </transition>
            <!-- module -->
            <fts-module :filter="drawerSearchKeyword" :workspace-builder="workspaceBuilder" />
            <div class="drawer-action-wrapper" v-if="!builderMode && isAdminUser">
              <div class="drawer-action" @click="openModuleDesignModal">
                <font-awesome-icon :icon="['fas', 'plus']" class="drawer-action-icon" />
                <div>Add a module</div>
              </div>
            </div>
          </template>
        </div>
        <div class="client-drawer-footer">
          <fts-profile
            :profile="profile"
            :profile-photo="false"
            profile-name-class="user-profile-info"
            class="client-user-profile"
            @click.native="openUserProfileModal"
          >
            <template #profileName="{ userProfileName, userProfileUsername }">
              <div class="user-profile-wrapper">
                <div class="user-profile-name">{{ userProfileName }}</div>
                <div class="user-profile-role">{{ userRoles }}</div>
              </div>
              <div
                class="user-profile-username client-drawer-username"
                v-if="userProfileUsername"
              >
                {{ userProfileUsername }}
              </div>
            </template>
          </fts-profile>
        </div>
      </div>
      <!-- content -->
      <div class="client-content" v-if="!workspaceLoading">
        <fts-run-as
          :workspace-id="workspaceId"
          :workspace-modules="workspaceModules"
          :run-as.sync="isRunAs"
          broadcast-mode
          @selected="setRunAs"
          v-if="isRunAs"
        />

        <router-view :key="routePath" />
        <!-- GNey AI teammate (alpha): movable mascot ⇄ docked chat ⇄ fullscreen,
             scoped to the current view/list — no workspace/module/model picker. -->
        <fts-client-teammate v-if="gneyDockMounted" @event:disable="toggleGneyAiEnable" />
        <!-- The Analytic Bench is NOT a rail here any more (user ruling 2026-08-23: no second
             panel, no popup) — it lives in the Dashboard page (Reports · My Insights tabs). Report
             cards and other callers reach it through the `gney:openReport` bridge in mounted(). -->
      </div>
      <!-- confirmation -->
      <v-dialog />
      <!-- user profile -->
      <fts-user-profile client ref="ftsUserProfile" />
      <!-- invitation -->
      <fts-invitation-modal ref="ftsInvitationModal" />
      <!-- workspace modal -->
      <fts-workspace-modal ref="ftsWorkspaceModal" />
      <!-- module design modal -->
      <fts-module-design-modal ref="ftsModuleDesignModal" />
      <!-- quick view -->
      <fts-quick-view
        :key="`qv-client-${quickViewKey}`"
        @closed="quickViewKey += 1"
      />
      <!-- content-notification reader (docs/notification-content-proposal.md §8.1) -->
      <fts-notification-reader
        :gney="gneyDockMounted"
        @read="onNotificationRead"
        @open-entry="onNotificationOpenEntry"
        @open-routine="onNotificationOpenRoutine"
      />
      <!--pdf view-->
      <fts-document-modal ref="documentModal" @closed="onDocumentModalClosed" />
      <!-- htmljs document view (engine === 'htmljs'): separate viewer, legacy path untouched -->
      <fts-html-js-document-modal ref="htmlJsDocumentModal" />
      <!-- module onboard modal -->
      <fts-module-onboard-modal
        client
        description="You have created the system successfully."
        @closed="startTour"
        ref="ftsModuleOnboardModal"
      />
      <!-- confirmation -->
      <fts-confirmation global />
      <!-- tour -->
      <v-tour
        name="clientTour"
        :steps="tourSteps()"
        :callbacks="{ 'onFinish': endTour, 'onStop': endTour }"
      />
      <!-- form skip confirmation -->
      <fts-form-skip-confirmation ref="ftsFormSkipConfirmation" />
      <!-- create workspace modal -->
      <fts-workspace-create-modal ref="workspaceCreateModal" @created="onWorkspaceCreated" />
    </template>

    <fts-spinner
      full-screen
      variant="rive"
      v-else
    />

    <fts-spinner
      full-screen
      variant="rive"
      class="client-loader"
      v-if="workspaceLoading"
    />
  </div>
</template>

<script>
import Vue from "vue";
import InfiniteLoading from "vue-infinite-loading";
import VueCookies from "vue-cookies";
Vue.use(VueCookies);
import VueTour from 'vue-tour';
require("vue-tour/dist/vue-tour.css");
Vue.use(VueTour);
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import signalr from "./components/store/signalr";

import webAppConfig from "./components/commons/webApp.config";
import commons from "./components/commons/commons";
import deepLinkWorkspaceMixin from "./components/commons/deepLinkWorkspaceMixin";
import { billingReturnSection, clearBillingReturn, readBillingReturn } from "./components/commons/billing";
import { buildDocumentRequest, sendWithTokenRefreshCallback, PDF_DOCUMENT_PATH, DOCUMENT_POST_CONTENT_TYPE } from "./components/commons/documentRequest";
import { queryWithOpenDocument, queryWithoutDocument } from "./components/device-preview/previewDocument";
import tours from "./components/commons/tours";
import { classificationStore } from "./components/store/classificationStore";
import { clientStore } from "./components/store/clientStore";
import { authorizationStore } from "./components/store/authorizationStore";
import { applyDisplay } from "./components/commons/readability";
import { settingsStageAllowed, settingsSectionAllowed } from "./components/settings/settingsStage";
import { settingsItem, settingsSectionFor } from "./components/settings/settingsNav";
import LocalStorageService from './components/store/LocalStorageService';
import selectionCache from './components/store/selectionCache';
import { markOnboardingInProgress } from './components/commons/onboardingProgress';

import FTSInvitationModal from "./components/client/FTSInvitationModal";
import FTSModule from "./components/design/FTSModule";
import FTSPortalNav from "./components/portals/FTSPortalNav";
import FTSModuleDesignModal from "./components/design/FTSModuleDesignModal";
import FTSWorkspaceModal from "./components/design/FTSWorkspaceModal";
import FTSDropdownSelect from "./components/elements/FTSDropdownSelect";
import FTSUserProfile from "./components/elements/FTSUserProfile";
import FTSProfile from "./components/elements/FTSProfile";
import FTSMoji from "./components/elements/FTSMoji";
import FTSBasket from "./components/elements/FTSBasket";
import FTSTippy from "./components/elements/FTSTippy";
import FTSSpinner from "./components/elements/FTSSpinner";
import FTSQuickView from "./components/elements/views/FTSQuickView";
import FTSDocumentModal from "./components/elements/FTSDocumentModal";
import FTSHtmlJsDocumentModal from "./components/elements/FTSHtmlJsDocumentModal";
import FTSTransitionExpand from "./components/elements/FTSTransitionExpand";
import FTSNotificationContent, { isContentNotification, opensInGneyDock, contentSourceLabel } from "./components/elements/notification/FTSNotificationContent";
import { approvalRequestIdOf } from "./components/approvals/approvalApi";
import FTSNotificationReader from "./components/elements/notification/FTSNotificationReader";
import FTSConfirmation from "./components/elements/confirmation/FTSConfirmation";
import FTSModuleOnboardModal from './components/design/FTSModuleOnboardModal';
import FTSFormSkipConfirmation from './components/elements/form/FTSFormSkipConfirmation.vue';
import FTSRunAs from './components/elements/FTSRunAs';
import FTSWorkspaceCreateModal from "./components/client/FTSWorkspaceCreateModal";
import FTSClientTeammate from "./components/elements/FTSClientTeammate";

let builderCallback = null;
const postEventMessage = Vue.prototype.$postEventMessage = (payload) => {
  try {
    let message = { type: "inistate", device: "web" };
    if (payload.path) {
      message.url = `${webAppConfig.baseLocationOrigin}/#${payload.path}`;
    }
    if (payload.onload) {
      message.onload = true;
    }
    if (payload.wsId) {
      message.action = "workspaceChanged";
      message.wsId = payload.wsId;
    }
    window.parent.postMessage(JSON.stringify(message), "*");
  } catch (err) {
    console.error(err);
  }
}

export default {
  metaInfo() {
    return { title: this.metaTitle };
  },
  mixins: [deepLinkWorkspaceMixin],
  components: {
    InfiniteLoading,
    "fts-invitation-modal": FTSInvitationModal,
    "fts-module": FTSModule,
    "fts-portal-nav": FTSPortalNav,
    "fts-module-design-modal": FTSModuleDesignModal,
    "fts-workspace-modal": FTSWorkspaceModal,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-user-profile": FTSUserProfile,
    "fts-profile": FTSProfile,
    "fts-moji": FTSMoji,
    "fts-basket": FTSBasket,
    "fts-tippy": FTSTippy,
    "fts-spinner": FTSSpinner,
    "fts-quick-view": FTSQuickView,
    "fts-document-modal": FTSDocumentModal,
    "fts-html-js-document-modal": FTSHtmlJsDocumentModal,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-notification-content": FTSNotificationContent,
    "fts-notification-reader": FTSNotificationReader,
    "fts-confirmation": FTSConfirmation,
    "fts-module-onboard-modal": FTSModuleOnboardModal,
    "fts-form-skip-confirmation": FTSFormSkipConfirmation,
    "fts-run-as": FTSRunAs,
    "fts-workspace-create-modal": FTSWorkspaceCreateModal,
    "fts-client-teammate": FTSClientTeammate,
  },
  data: () => ({
    prefReady: false,
    contentReady: false,
    isDrawerStatic: true,
    isDrawerVisible: true,
    drawerSearchKeyword: null,
    drawerSearchOpen: false,
    /* workspace */
    workspaceLoading: false,
    workspacesLoading: false,
    workspacesSearching: false,
    workspaceSearch: "",
    workspaceOptionList: [],
    workspaceOptionRequest: 0,
    workspaceContextSearch: null,
    previousWorkspaceId: null,
    // The vendor company the signed-in account acts for ({ id, name }), or null.
    vendor: null,
    /* context */
    contextOptionList: [],
    /* notification */
    unreadCount: 0,
    notifications: [],
    notifCurrentPage: 0,
    notifReady: false,
    /* pre workspaces */
    preWorkspaces: [],
    /* quick view */
    quickViewKey: 0,
    // run as
    isRunAs: false,
    // real-time changes
    workspaceRTCStatus: "",
    userRTCStatus: "",
    inactivityStartTime: null,
    workspaceVersionInterval: null,
    workspaceChangedTimeout: null,
    userChangedTimeout: null,
    currentUserChangedTimeout: null,
    enabledGneyAi: false,
  }),
  methods: {
    toggleDrawerSearch() {
      /* an open field with an active keyword clears itself; otherwise reveal and focus */
      if (this.drawerSearchOpen) {
        this.closeDrawerSearch(true);
        return;
      }
      this.drawerSearchOpen = true;
      this.$nextTick(() => {
        if (this.$refs.drawerSearchInput) this.$refs.drawerSearchInput.focus();
      });
    },
    closeDrawerSearch(clear) {
      if (clear) this.drawerSearchKeyword = null;
      /* keep the field visible while it still filters the module list */
      if (this.drawerSearchKeyword) return;
      this.drawerSearchOpen = false;
    },
    toggleDrawerMode() {
      this.isDrawerStatic = !this.isDrawerStatic;
      if (!this.isDrawerStatic) {
        this.isDrawerVisible = false;
      }
      LocalStorageService.setPreference({
        ...LocalStorageService.getPreference(),
        collapse: !this.isDrawerStatic
      });
    },
    sidebarMouseEvent: _.debounce(function(evt) {
      if (this.isDrawerStatic === false) {
        if (evt.type === 'mouseenter') {
          this.isDrawerVisible = true;
        } else if (evt.type === 'mouseleave') {
          if (
            evt && evt.toElement &&
            (
              this.$refs.sidenav && this.$refs.sidenav.contains(evt.toElement)
              || this.$refs.drawer && this.$refs.drawer.contains(evt.toElement)
              || commons.elementContainClassName(evt.toElement, 'sidebar-relative-el')
            )
          ) { return; }
          this.isDrawerVisible = false;
        }
      }
    }, 300),
    moduleData(moduleId) {
      const module = (clientStore.state.workspaceModules || []).find(
        x => x.id == moduleId
      ) || {};

      return {
        id: module.id,
        name: module.name,
        emoji: module.emoji,
      };
    },
    openInvitation() {
      this.$refs.ftsInvitationModal.open({ workspaceName: this.workspace.name });
    },
    async openUserProfileModal() {
      // Your avatar is the way into settings: Settings › You. The account dialog is only for
      // builder sessions and for when the kill switch is thrown (W-25) - and for the lobby: a
      // user waiting to be let into a workspace is held on /lobby by beforeRouteUpdate, so the
      // push would bounce straight back and the dialog is their only Log out (AWT
      // invitationAndUser "Password Policy Registration").
      if (!this.builderMode && settingsStageAllowed() && !this.workspaceLobby) {
        this.$router.push({ name: "settings", params: { section: "profile" } }).catch(() => {});
        return;
      }
      await this.$refs.ftsUserProfile.show();
    },
    async setDefaultWorkspaceContext() {
      const workspaceContexts = await clientStore.dispatch("getWorkspaceContexts", {
        search: "" 
      });

      if (workspaceContexts && workspaceContexts.length > 0) {
        clientStore.dispatch("setWorkspaceContext", workspaceContexts[0]);
      }
    },
    async loadWorkspaceCache(workspaceId) {
      const lsWorkspaceData = LocalStorageService.getWorkspace(webAppConfig.baseServer, workspaceId);
      if (lsWorkspaceData && lsWorkspaceData.id) {
        try {
          clientStore.dispatch("setWorkspace", { workspace: lsWorkspaceData });
          
          if (this.workspace.hasContext && !this.isWorkspaceContext) {
            await this.setDefaultWorkspaceContext();
          }

          return true;
        } catch(err) {
          LocalStorageService.clearWorkspace(webAppConfig.baseServer);
        }
      }

      return false;
    },
    async loadWorkspace(payload, option) {
      let isInitial = !!(option && option.isInitial);
      let requestError = null;
      let isStale = false;

      const _loadWorkspace = async (requestPayload) => {
        try {
          /* SS05685/SS05686: an emailed entry link carries the module but no workspace, so
             bootstrapping from the cached workspace id fired the form request against whichever
             workspace the recipient happened to be in — and the server, which scopes listing
             grants to that header, answered "No accessible listing". Resolve the owning
             workspace from the module instead, as Lite already does. */
          const loadWorkspaceAction =
            requestPayload && requestPayload.moduleId
              ? "loadWorkspaceByModuleId"
              : "loadWorkspace";
          const result = await clientStore.dispatch(loadWorkspaceAction, requestPayload);
          if (result && result.stale) {
            isStale = true;
            return;
          }
          const signalrConnectionState = signalr.getConnectionState();
          if (this.previousWorkspaceId != this.workspaceId || signalrConnectionState === "disconnected") {
            const maxRetries = 3;
            for (let retryCount = 1; retryCount <= maxRetries; retryCount++) {
              try {
                await signalr.setupCommunication(this.workspaceId);
                break;
              } catch(signalrError) {
                console.error(`Failed to setup communication. Attempt ${retryCount} of ${maxRetries}`, signalrError);
                await new Promise(resolve => setTimeout(resolve, 3000));
                if (retryCount === maxRetries) {
                  console.error("Failed to setup communication", signalrError);
                }
              }
            }
          }
          requestError = null;

          if (this.workspaceLobby && this.$route.name !== "lobby") {
            this.$router.replace({ name: "lobby" });
          }
        } catch(err) {
          console.error("Failed to load workspace", err);
          requestError = err;
        }
      }

      await _loadWorkspace(payload);

      if (isStale) {
        return { stale: true };
      }

      /* SS06178/SS06180: a background refresh keeps the workspace it already has. Redirecting and
         reloading "a" workspace here - with the remembered id gone - landed the user in the first
         workspace of their list after an intermittent 500. */
      if (requestError && payload && payload.background) {
        throw requestError;
      }

      if (requestError) {
        if (!isInitial) {
          this.$toasted.global.error(
            requestError,
            "Oops! Unable to load the workspace at the moment. Redirecting..."
          );

          if (this.$route.name != "dashboard") {
            this.$router.replace({ name: "dashboard" });
          }

          clientStore.dispatch("resetModule");
        }

        await _loadWorkspace();
      }

      if (requestError) {
        throw requestError;
      }

      // redirect to workspace home based on type: lite → lite, else → dashboard.
      // Settings is a destination, not a home: a lite workspace opening it must stay
      // there rather than being thrown back to the lite listing.
      if (this.isLiteMode && this.$route.name !== "settings") {
        this.$router.replace({ name: "lite" });
      }

      return { stale: false };
    },
    async loadPreWorkspaces() {
      this._preWorkspacesRequestId = (this._preWorkspacesRequestId || 0) + 1;
      const requestId = this._preWorkspacesRequestId;

      try {
        const res = await clientStore.dispatch("getWorkspaces", {
          search: ""
        });

        // a newer call to loadPreWorkspaces() has started since this one began
        // (e.g. the user switched workspace again) — discard this stale result
        if (requestId !== this._preWorkspacesRequestId) {
          return;
        }

        this.preWorkspaces = [{
          id: this.workspace.id,
          name: this.workspace.name,
          shortName: commons.getWorkspaceShortName(this.workspace.name)
        }].concat(
          ...res
            .filter(x => x.id != this.workspace.id)
            .slice(0, 2)
            .map(x => ({
              id: x.id,
              name: x.name,
              shortName: commons.getWorkspaceShortName(x.name)
            }))
        );
      } catch(err) {
        console.error(err);
      }
    },
    async showWorkspaceOptions() {
      this.workspacesLoading = true;
      this.workspaceSearch = "";
      await this.loadWorkspaceOptions(this.workspaceSearch);
      this.workspacesLoading = false;
      this.$nextTick(() => {
        this.$refs.workspaceSearch && this.$refs.workspaceSearch.focus();
      });
    },
    /**
     * Fills the workspace switcher's list. Only the newest request may: opening fetches every
     * workspace and typing fetches the matches 500 ms later, so a slow opening response used to
     * land last and put the full list back under the search text (Automated Web Tests #629 never
     * found "Routine Auto Test"; #624's pick of "FETIAS S3 Test" landed on another row).
     */
    async loadWorkspaceOptions(search) {
      const request = ++this.workspaceOptionRequest;
      const list = await clientStore.dispatch("getWorkspaces", { search });
      if (request !== this.workspaceOptionRequest) return;
      this.workspaceOptionList = list;
      this.workspacesSearching = false;
    },
    async selectWorkspace(workspace) {
      if (workspace.id != this.workspace.id) {
        try {
          this.workspaceLoading = true;

          if (this.isRunAs) {
            this.isRunAs = false;
            authorizationStore.dispatch("setRunAs", { workspaceId: workspace.id });
          }

          clientStore.dispatch("resetModule");
          const hasCache = await this.loadWorkspaceCache(workspace.id);

          // redirect to workspace home: lite → lite (handled in loadWorkspace), else → dashboard
          if (this.$route.name != "dashboard" && !this.isLiteMode) {
            this.$router.push({ name: "dashboard" });
          }

          if (hasCache && !this.isLiteMode) {
            this.workspaceLoading = false;
          }

          const result = await this.loadWorkspace({
            id: workspace.id,
            client: true,
          });
          if (result && result.stale) {
            return;
          }
          // load default workspace context
          if (this.workspace.hasContext && !this.isWorkspaceContext) {
            await this.setDefaultWorkspaceContext();
          }

          if (!this.builderMode && this.shouldPromptFirstModule()) {
            this.openModuleDesignModal();
          }
        } catch(err) {
          console.error(err);
          location.reload(); // to-do error
        } finally {
          this.workspaceLoading = false;
        }
      }
    },
    /**
     * Back from Stripe Checkout: the server sends the customer to the app origin with
     * `?billing=success|pack|cancel&ws=<workspaceId>` (a server query, which the hash router
     * leaves alone). Open that workspace's Plan & Credits tab so it can confirm the outcome —
     * nothing else in the app reacts to the query, so without this the customer lands on the
     * dashboard with no sign that the payment went through.
     * A billing email's button (`?billing=open&to=billing|credits|plan|autocount&ws=…`) takes the
     * same road to the page it names; it has nothing to confirm, so its query is cleared straight
     * away. AutoCount Full's own Checkout (`&connector=autocount`) opens Settings › AutoCount,
     * which confirms that purchase.
     */
    async openBillingReturn() {
      if (this.builderMode) return;
      const ret = readBillingReturn(window.location.search);
      if (!ret) return;
      if (ret.kind === "open") clearBillingReturn();
      try {
        if (ret.workspaceId && ret.workspaceId != this.workspaceId) {
          await this.selectWorkspace({ id: ret.workspaceId });
        }
        // An email can reach someone outside that workspace (a billing contact is any address):
        // never open this workspace's billing in place of the one the email was about.
        if (ret.kind === "open" && ret.workspaceId && ret.workspaceId != this.workspaceId) return;
        await this.selectWorkspaceOption("edit", { section: "subscription", settingsSection: billingReturnSection(ret) });
      } catch (err) {
        console.error("billing return", err);
      }
    },
    /**
     * The workspace in view is gone for this person: deleted (the old dialog or Settings ›
     * General), or they left it (Settings). Move them to another one they belong to, and drop it
     * from the switcher; with none left, reload into the app's own no-workspace start.
     */
    async moveOffWorkspace(goneId) {
      this.workspaceLoading = true;
      let selectedWorkspaceId = null;

      if (Array.isArray(this.preWorkspaces)) {
        const preWorkspace = this.preWorkspaces.find(x => x.id != goneId);
        selectedWorkspaceId = preWorkspace && preWorkspace.id;
      }

      if (!selectedWorkspaceId) {
        selectedWorkspaceId = await clientStore.dispatch("getFirstOrDefaultWorkspaceId");
      }

      if (selectedWorkspaceId && selectedWorkspaceId != goneId) {
        await this.selectWorkspace({ id: selectedWorkspaceId });
        this.loadPreWorkspaces();
      } else {
        location.reload();
      }
    },
    async selectWorkspaceOption(item, modalOptions) {
      if (item == "edit") {
        // Workspace settings: everyone lands in the settings area (GA). This dialog is the
        // fallback when the kill switch is thrown, and for `legacy: true` (a settings section
        // with no page of its own; none is left). W-25.
        if (settingsStageAllowed() && !(modalOptions && modalOptions.legacy)) {
          const section = settingsSectionFor(modalOptions);
          this.$router.push({ name: "settings", params: { section } }).catch(() => {});
          return;
        }
        const res = await this.$refs.ftsWorkspaceModal.show({ id: this.workspaceId, ...(modalOptions || {}) });
        if (res && res.name) {
          clientStore.dispatch("setWorkspaceName", res.name);
          let preWorkspace = this.preWorkspaces.find(x => x.id == res.id);
          preWorkspace && (preWorkspace.name = res.name);
        } else if (res && res.type == "delete") {
          await this.moveOffWorkspace(res.id);
        } else if (res && res.id) {
          await this.selectWorkspace({ id: res.id });
        }
      } else if (item == "create") {
        this.$refs.workspaceCreateModal.open();
      } else if (item == "createForCustomer") {
        this.$refs.workspaceCreateModal.open({ vendor: this.vendor });
      } else if (item == "runAs") {
        this.isRunAs = true;
      } else if (item == "requestMobileView") {
        commons.redirectMobileView({
          appUrl: webAppConfig.previewAppURL,
          baseUrl: webAppConfig.baseURL,
          accessToken: LocalStorageService.getAccessToken(),
          refreshToken: LocalStorageService.getRefreshToken(),
          webData: LocalStorageService.getWebData(webAppConfig.baseServer),
          workspaceId: this.workspaceId
        });
      } else {
        this.selectWorkspace(item);
      }
    },
    async showContextOptions() {
      this.workspaceContextSearch = "";
      this.contextOptionList = await clientStore.dispatch(
        "getWorkspaceContexts",
        { search: this.workspaceContextSearch}
      );

      this.$nextTick(() => {
        this.$refs.workspaceContextSearch.focus();
      });
    },
    selectWorkspaceContext(res) {
      clientStore.dispatch("setWorkspaceContext", res);
      window.eventBus.$emit("listing:refresh");
    },
    async infiniteHandler($state) {
      const res = await this.loadNotification();
      if (res) {
        this.notifCurrentPage += 1;
        $state.loaded();
      } else {
        $state.complete();
      }
    },
    async openQuickView(item) {
      if (!item) return;
      // Content notification (docs/notification-content-proposal.md §8.1): with a subject
      // entry the row opens that entry directly (C2); otherwise the reader. Either way the
      // detail call marks it read server-side.
      if (isContentNotification(item)) {
        item.read = true;
        // An approval request (docs/teammate-human-approval-request-proposal.md) opens the
        // request, where the person approves or rejects it, rather than the entry it is about.
        const approvalRequestId = approvalRequestIdOf(item);
        if (approvalRequestId) {
          clientStore.dispatch("retrieveNotificationDetail", item.id).catch(() => {});
          this.$router.push({ name: "approval-request", params: { id: approvalRequestId } });
          return;
        }
        if (item.about && item.about.locusId) {
          clientStore.dispatch("retrieveNotificationDetail", item.id).catch(() => {});
          window.eventBus.$emit("quickViewData", {
            id: item.about.locusId,
            vectorId: item.about.vectorId,
          });
          return;
        }
        // D11: GNey / routine content opens INTO the dock as a card (fresh conversation, composer
        // beneath) when the dock is mounted; system / report rows and shells without a dock keep
        // the reader.
        if (this.gneyDockMounted && opensInGneyDock(item)) {
          window.eventBus.$emit("gney:openNotification", {
            id: item.id,
            title: item.title || "Notification",
            label: contentSourceLabel(item),
          });
          return;
        }
        window.eventBus.$emit("notificationOpen", item.id);
        return;
      }
      // An entry-bound row without an entry (legacy rows) has nowhere to go.
      if (!item.locusId) return;
      window.eventBus.$emit("quickViewData", {
        id: item.locusId,
        vectorId: item.vectorId,
      });
    },
    onNotificationRead(id) {
      const row = this.notifications.find((x) => x.id === id);
      if (row) row.read = true;
    },
    onNotificationOpenEntry(payload) {
      window.eventBus.$emit("quickViewData", { id: payload.id, vectorId: payload.vectorId });
    },
    onNotificationOpenRoutine(payload) {
      this.$router.push({ name: "routines", query: { routine: String(payload.routineId) } }).catch(() => {});
    },
    /* notification */
    async openNotification() {
      // SS06219: list only once the "unreceived" clear has landed. That call re-saves every
      // unreceived reading, and a list query running alongside it can skip a row being re-saved -
      // the newest rows, the ones the bell is opened for (AWT #646 showed one of two fresh routine
      // rows; on 132 the parallel pair lost a row in 2 of 18 rounds, the list alone in 0 of 12).
      // The loader mounts behind notifReady, so every opening also starts it afresh.
      this.notifReady = false;
      this.notifications = [];
      this.notifCurrentPage = 0;
      try {
        await clientStore.dispatch("clearNotificationUnreceivedCount");
        this.unreadCount = 0;
      } catch (err) {
        console.error(err);
      } finally {
        this.notifReady = true;
      }
    },
    async loadNotification() {
      try {
        const res = await clientStore.dispatch("retrieveNotification", {
          pageSize: 10,
          currentPage: this.notifCurrentPage,
        });

        if (res.data && res.data.list && res.data.list.length) {
          res.data.list.forEach((x) => {
            if (this.notifications.findIndex(y => y.id === x.id) === -1) {
              x.shortMessage = _.truncate(x.message, { 'length': 200 });
              this.notifications.push(x);
            }
          });

          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    toggleNotificationGroup(item) {
      if (item.expand)
        this.$delete(item, 'expand');
      else
        this.$set(item, 'expand', true);
    },
    notificationGroupUnreadCount(list) {
      const count = list.filter(x => !x.read).length;

      if (count) {
        if (count === 1)
          return '1 notification';
        else
          return `${count} notifications`;
      }
    },
    /* SS06072: "99+" past the cap, identical on Web and the mobile app */
    formatBadgeCount(count) {
      return commons.formatBadgeCount(count);
    },
    async loadNotificationCount() {
      let res2 = await clientStore.dispatch("retrieveNotificationCount");
      this.unreadCount = res2.data && res2.data.count;
    },
    /* module */
    /**
     * Whether an empty workspace should ask its admin for the first module on its own. Not on
     * the settings page: settings is a child of this shell, so the Studio and Lite avatars (the
     * way into settings since it went live) MOUNT this shell straight onto Settings > Profile,
     * and the dialog would sit over the page and its Log out (AWT #626 invitationAndUser). The
     * drawer's "Add a module" still offers it from the workspace.
     */
    shouldPromptFirstModule() {
      if (!this.isAdminUser || this.settingsOpen) return false;
      return !(this.workspaceModules && this.workspaceModules.length);
    },
    async openModuleDesignModal() {
      let loadWorkspaceError = false;
      const res = await this.$refs.ftsModuleDesignModal.show({
        workspaceId: this.workspaceId,
        beforeResolve: async () => {
          try {
            await clientStore.dispatch("loadWorkspace", {});
          } catch(err) {
            loadWorkspaceError = true;
          }
        }
      });
      if (res) {
        /* Drop the author straight into the designer to lay the new module's form out — but
           only for whoever the edit-mode toggle itself shows for (alpha, internal admins;
           see FTSListView). "Add a module" is open to every admin, and an empty workspace
           opens this modal on its own, so without the gate a customer admin would land in
           edit mode with no pen-ruler icon to leave by. */
        if (this.isInternal) {
          clientStore.dispatch("setDesignMode", { mode: true });
        }
        this.$router.push({
          name: 'list-view',
          params: { moduleId: commons.encodeids(res) },
          query: { t: 'dv' }
        });
        loadWorkspaceError && location.reload();
      }
    },
    async openModuleOnboardModal() {
      this.$refs.ftsModuleOnboardModal.show();
    },
    startTour() {
      this.$tours['clientTour'].start();
    },
    endTour() {
      $cookies.get('client_onboarding') && $cookies.remove('client_onboarding');
    },
    tourSteps() {
      return this.$route.name === 'list-view' ?
        [...tours.clientTourSteps, ...tours.clientCreateEntrySteps] :
        [...tours.clientTourSteps];
    },
    /* SS06258: the PDF closed - in the preview it leaves the entry's route, so the other device closes it */
    onDocumentModalClosed() {
      if (!this.builderMode) return;
      const query = queryWithoutDocument(this.$route.query);
      if (query) this.$router.replace({ query }).catch(() => {});
    },
    builderActionMessage(evt) {
      if (
        evt.data &&
        (typeof evt.data === 'string' || evt.data instanceof String) &&
        evt.data.indexOf('inistate') > -1
      ) {
        try {
          const res = JSON.parse(evt.data);
          if (res.url && res.redirect !== false) {
            builderCallback = false;
            let route = this.$router.resolve(res.url.substring(res.url.indexOf('#') + 1)).route;
            // SS06258: the PDF document the other device has open over the entry (?documentid=..), or
            // none any more - then the one open here closes
            const previewDocumentId = route.query.documentid;
            const closesPreviewDocument = !previewDocumentId && Boolean(this.$router.currentRoute.query.documentid);
            // the quick view opens it once that entry has loaded, and keeps it on the entry's route
            if (previewDocumentId && route.query.id) {
              window.eventBus.$emit("previewOpenDocument", {
                documentId: previewDocumentId,
                entryId: commons.queryDecodeids(route.query.id).entryId,
              });
            }
            if (route.query.refresh === 'true') {
              if ((route.query.a || route.query.id)) {
                const currentRoute = this.$router.currentRoute;
                if (
                  (Boolean(currentRoute.query.a) && currentRoute.query.a === route.query.a) ||
                  (Boolean(currentRoute.query.id) && currentRoute.query.id === route.query.id)
                ) {
                  window.eventBus.$emit('ftsQuickViewLoadForm');
                } else {
                  this.$router.push(route);
                }
              } else {
                if (authorizationStore.getters.isRunAs) {
                  route.query.runas = authorizationStore.state.runAs.target;
                }
                this.$router.push(route);
                window.location.reload();
              }
            } else if (route.query.hasOwnProperty('runas')) {
              this.setRunAs({
                target: !!route.query.runas ? route.query.runas : null,
                loading: (val) => this.workspaceLoading = val,
                error: (val) => this.$toasted.global.error({ message: val })
              });
            } else if (
              route.name === "dashboard" && route.query.id &&
              this.$router.currentRoute.name === "dashboard"
            ) {
              // SS06260: the dashboard reads ?id only on mount - open the entry the other device has over its
              // home in this dashboard's quick view (which puts id/a on the route itself)
              const ids = commons.queryDecodeids(route.query.id);
              window.eventBus.$emit("quickViewData", { id: ids.entryId, vectorId: ids.moduleId, activityId: route.query.a });
            } else {
              // SS06257: the other device left the entry - close the quick view wherever Desktop is. The
              // dashboard reads ?id only on mount, and a page reached from it doesn't close it either.
              if (!route.query.id && this.$router.currentRoute.query.id) {
                window.eventBus.$emit("closeQuickView");
              }
              const navigation = this.$router.push(route);
              // closing the quick view may already have navigated there
              if (navigation && navigation.catch) navigation.catch(() => {});
            }
            if (closesPreviewDocument && this.$refs.documentModal) {
              this.$refs.documentModal.close();
            }
            builderCallback = null;
          }
        } catch (err) {
          console.error(err);
        }
      }
    },
    async setRunAs(payload) {
      try {
        payload.loading && payload.loading(true);
        authorizationStore.dispatch("setRunAs", {
          workspaceId: this.workspaceId,
          target: payload.target
        });

        await clientStore.dispatch("loadWorkspace", {
          id: this.workspaceId,
          forceReload: true,
          preserveMenuState: true
        });

        if (this.$route.name == "dashboard") {
          window.eventBus.$emit("dashboardRefresh");
        } else if (this.$route.name == "calendar") {
          window.eventBus.$emit("calendarRefresh");
        } else if (this.$route.name == "list-view") {
          window.eventBus.$emit("listing:schemaUpdated");
        }
      } catch(err) {
        console.error(err);
        payload.error && payload.error(err.response && err.response.data && err.response.data.message || "Something went wrong");
      } finally {
        payload.loading && payload.loading(false);
      }
    },
    setWorkspaceVersionInterval() {
      if (this.workspaceVersionInterval) {
        clearInterval(this.workspaceVersionInterval);
        this.workspaceVersionInterval = null;
      }

      this.workspaceVersionInterval = setInterval(() => {
        if (this.inactivityStartTime) {
          const now = new Date;
          const downtime = now - this.inactivityStartTime;
          if (downtime > 3600000) {
            if (this.workspaceVersionInterval) {
              clearInterval(this.workspaceVersionInterval);
              this.workspaceVersionInterval = null;
            }
            return;
          }
        }

        if (!navigator.onLine) {
          return;
        }

        this.loadWorkspace({
          id: this.workspaceId,
          preserveMenuState: true,
          background: true
        }).catch((err) => console.error(err));
      }, 3600000);
    },
    async handleVisibilityChange() {
      if (document.hidden) {
        this.inactivityStartTime = new Date;

        if (authorizationStore.getters.isDevelopment) {
          console.info("Client:Inactive", `${moment(this.inactivityStartTime).format('YYYY-MM-DD HH:mm:ss')}`);
        }
      } else {
        const now = new Date;
        const downtime = now - this.inactivityStartTime;
        this.inactivityStartTime = null;

        if (authorizationStore.getters.isDevelopment) {
          const formatDowntime = (downtime) => {
            const duration = moment.duration(downtime);
            const hours = Math.floor(duration.asHours());
            const minutes = Math.floor(duration.minutes());
            const seconds = Math.floor(duration.seconds());
            return `${hours}h ${minutes}m ${seconds}s`;
          }
          console.info("Client:Active", `${moment(now).format('YYYY-MM-DD HH:mm:ss')}, downtime: ${formatDowntime(downtime)}`);
        }

        if (downtime > 3600000) {
          this.setWorkspaceVersionInterval();

          if (!navigator.onLine) {
            return;
          }

          this.loadWorkspace({
            id: this.workspaceId,
            preserveMenuState: true,
            background: true
          }).catch((err) => console.error(err));
        }
      }
    },
    isMobileView() {
      return this.isInternal && /iPhone|iPad|iPod|Android|Tablet/i.test(navigator.userAgent);
    },
    toggleGneyAiEnable (){
      this.enabledGneyAi = !this.enabledGneyAi;
      localStorage.setItem("gney-ai-enabled",  this.enabledGneyAi);
    },
    toggleDemoMode() {
      authorizationStore.dispatch("setDemoMode");
    },
    /* Only a vendor's people get the customer entry. A probe that fails hides it, as for everyone else. */
    async loadVendor() {
      try {
        this.vendor = await authorizationStore.dispatch("getVendor");
      } catch (err) {
        this.vendor = null;
      }
    },
    /** `customer` comes from the vendor's form: { autocount } says where the new workspace starts. */
    async onWorkspaceCreated(workspaceId, customer) {
      await this.loadWorkspace({ id: workspaceId, client: true });

      if (this.workspaceId != workspaceId) {
        this.$toasted.global.error(new Error("Workspace was created, but we couldn't switch to it. Please refresh and try again."));
        return;
      }

      if (customer) {
        // N3 and VL-10: Settings › AutoCount makes the pairing code as it opens; without AutoCount
        // the guided wizard picks up at its questions and first workflow for this workspace.
        this.$nextTick(() => {
          if (customer.autocount) {
            this.$router.replace({ name: 'settings', params: { section: 'autocount' } }).catch(() => {});
          } else {
            markOnboardingInProgress(workspaceId);
            this.$router.replace({ name: 'setup-workspace' }).catch(() => {});
          }
        });
        return;
      }

      if (this.isAdminUser) {
        this.$nextTick(() => {
          this.$router.replace({ name: 'studio' });
        });
      }
    },
  },
  watch: {
    workspaceId(val, oldVal) {
      if (val != oldVal) {
        this.previousWorkspaceId = oldVal;
        this.unreadCount = 0;
        this.loadNotificationCount();
        this.loadPreWorkspaces();

        // SS06256: the device preview re-points its other device at the workspace chosen here
        if (this.builderMode && val) {
          postEventMessage({ wsId: val });
        }
      }
    },
    workspaceSearch: _.debounce(async function () {
      this.workspacesSearching = true;
      await this.loadWorkspaceOptions(this.workspaceSearch);
    }, 500),
    workspaceContextSearch: _.debounce(async function () {
      this.contextOptionList = await clientStore.dispatch(
        "getWorkspaceContexts",
        { search: this.workspaceContextSearch}
      );
    }, 500),
  },
  computed: {
    /**
     * The GNey dock is mounted in this shell (the <fts-client-teammate> v-if). GNey is on in every
     * workspace for everyone (2026-10-19, no more workspace `gney` flag): a turn with no AI credits
     * left meets the server's 402 and the chat shows the top-up card (teammateBlocks.js). Only the
     * Lobby keeps it out - a pending-join user has no workspace to work in yet.
     */
    gneyDockMounted() {
      return !!(this.enabledGneyAi && !this.workspaceLobby);
    },
    metaTitle() {
      let title = ['Inistate'];
      this.workspace.name && title.push(this.workspace.name);
      this.module.name && title.push(this.module.name);
      return title.reverse().join(' - ');
    },
    isLiteMode() {
      return clientStore.state.isLiteMode;
    },
    designMode() {
      return clientStore.getters.designMode;
    },
    sidenavLogo() {
      return authorizationStore.getters.appLogo;
    },
    designRoute() {
      let route = { name: 'studio', query: {}};
      if (this.module.id)
        route.query.id = commons.encodeids(this.module.id);
      else
        route.query.wsId = this.workspaceId;
      return route;
    },
    routePath() {
      return `${this.$route.path}/${this.$route.query.l}`;
    },
    workspace() {
      return clientStore.state.workspace || {};
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceContext() {
      return clientStore.state.workspaceContext || {};
    },
    isWorkspaceContext() {
      return this.workspaceContext && this.workspaceContext.hasOwnProperty("id");
    },
    workspaceBuilder() {
      return clientStore.getters.workspaceBuilder;
    },
    workspaceLobby() {
      return clientStore.getters.workspaceLobby;
    },
    module() {
      return clientStore.state.module || {};
    },
    moduleId() {
      return clientStore.getters.moduleId;
    },
    list() {
      return clientStore.state.list || {};
    },
    profile() {
      return authorizationStore.getters.userProfile;
    },
    userRoles() {
      return authorizationStore.getters.userRoles;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    isManagerUser() {
      return authorizationStore.getters.isManagerUser;
    },
    // Device preview is live for every workspace admin (no longer alpha, internal-only); the
    // preview page turns anyone else away itself.
    showDevicePreview() {
      return this.isAdminUser && !this.builderMode;
    },
    // "Manage workspace" left the workspace menu once settings went live: the avatar is the one way
    // in (KY, 2026-10-01). It comes back only with the kill switch, as the old dialog's door.
    manageWorkspaceInMenu() {
      return !settingsStageAllowed();
    },
    // The people page is Settings > People & seats, straight there rather than by the /users
    // redirect; /users itself again when the kill switch is thrown (W-25).
    usersRoute() {
      return settingsSectionAllowed(settingsItem("people"))
        ? { name: "settings", params: { section: "people" } }
        : { name: "users" };
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    // API connections (MCP connectors): feature enabled AND this user's seat
    // tier qualifies under the pricing gate.
    connectionsAvailable() {
      return authorizationStore.getters.connectionsAvailable;
    },
    appsAvailable() {
      return authorizationStore.getters.appsAvailable;
    },
    /*
     * Integrations (alpha) sidenav group: Routines, Connections and Apps. Each
     * entry carries the gate it had as a standalone rail icon, so nothing here
     * widens access — the group itself only renders when at least one survives.
     */
    integrationsMenu() {
      if (!this.isInternal) return [];

      const menu = [
        {
          route: "routines",
          title: "Routines",
          description: "Scheduled and recurring automations",
          icon: "clock",
          available: true,
        },
        {
          route: "connections",
          title: "Connections",
          description: "API tokens and MCP connectors",
          icon: "plug",
          available: this.connectionsAvailable,
        },
        {
          route: "apps",
          title: "Apps",
          description: "Install modules from the Module Store",
          icon: "bolt",
          available: this.appsAvailable,
        },
      ];

      return menu.filter(item => item.available);
    },
    integrationsActive() {
      return this.integrationsMenu.some(item => item.route === this.$route.name);
    },
    // Raw identity — stays true in demo mode so the demo toggle stays visible.
    isInternalIdentity() {
      return authorizationStore.getters.isInternalIdentity;
    },
    demoMode() {
      return authorizationStore.getters.demoMode;
    },
    isBeta() {
      return authorizationStore.getters.isBeta;
    },
    // The workspace settings area (alpha) is full-window: it carries its own sidebar,
    // so the shell's rail and drawer step aside while it is open.
    settingsOpen() {
      return this.$route && this.$route.name === "settings";
    }
  },
  created() {
    this.builderMode = commons.builderMode;
  },
  async mounted() {
    // builder mode
    if (this.builderMode) {
      window.addEventListener("message", this.builderActionMessage, false);
    }
    // web app config
    await webAppConfig.getConfig();
    // log
    $cookies.set("loggedSource", "client");
    // set theme
    const setTheme = (theme) => {
      if (theme === 'dark') {
        document.body.classList.add('dark');
        localStorage.setItem("fetias-client-theme", 'dark');
      } else {
        document.body.classList.remove('dark');
        localStorage.setItem("fetias-client-theme", 'light');
      }
    }
    // window match media — keep refs so the listener is removed on destroy (avoids leaking
    // the closure/instance across Client re-mounts).
    this._darkModeMql = window.matchMedia('(prefers-color-scheme: dark)');
    this._onColorSchemeChange = (event) => {
      if (Boolean(localStorage["fetias-client-theme-sync-os"])) {
        event.matches ? setTheme('dark') : setTheme('light');
      }
    };
    this._darkModeMql.addEventListener('change', this._onColorSchemeChange);

    if (
      (
        Boolean(localStorage["fetias-client-theme-sync-os"]) &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      ) ||
      (
        !Boolean(localStorage["fetias-client-theme-sync-os"]) &&
        localStorage['fetias-client-theme'] === 'dark'
      )
    ) {
      setTheme('dark');
    } else {
      setTheme('light');
    }
    // reading preference: this person's own text size and contrast on this device (FTR00375)
    applyDisplay(authorizationStore.getters.userName);
    // ls.preference
    const localStoragePreference = {...LocalStorageService.getPreference()};
    if (localStoragePreference.collapse) {
      this.isDrawerStatic = false;
      this.isDrawerVisible = false;
    }
    if (this.$route.query.start === 'basic' && this.isDrawerStatic) {
      this.toggleDrawerMode();
    }
    // preference ready
    this.prefReady = true;
    // color
    const color = localStorage['fetias-client-color'];
    if (color) {
      try {
        this.$nextTick(() => {
          this.$refs.client.classList.add(color);
        });
      } catch(err) {
        console.warn('ClientColor', err);
      }
    }

    let workspaceId = $cookies.get('wsId');

    if (this.$route.query && this.$route.query.wsId) {
      workspaceId = commons.decodeids(this.$route.query.wsId);
      let query = _.cloneDeep(this.$route.query);
      delete query.wsId;
      this.$router.replace({ query: query });
    }
    // profile
    await authorizationStore.dispatch("profile");
    this.loadVendor();
    // pdf document
    this._onShowPDFDocument = async (payload) => {
      let vm = this;

      // htmljs documents render through the new sandboxed viewer, never the legacy PdfSharp/HTML
      // path below. Branch first so all existing typeId handling is left exactly as-is.
      if (payload.document && payload.document.engine === "htmljs") {
        vm.$refs.htmlJsDocumentModal.show(payload);
        return;
      }

      let parameters = {
        documentId: payload.document.id,
        vectorId: payload.moduleId,
        locusIds:
          payload.items && payload.items.length
            ? payload.items.map((x) => x.id)
            : null,
        locusId:
          payload.items && payload.items.length ? payload.items[0].id : null,
        list: payload.listingId,
        queryFilters: payload.filters,
        inputs: payload.inputs,
      };

      let documentModel = { parameters: parameters };
      // the htmlViewer route reads the model back out of `file` after this exact prefix
      let prefixHTMLUrl =
        "/service/htmltemplateprocessing/document?documentmodel=";
      classificationStore.dispatch("updateDocumentId", null);

      if (
        payload.document.typeId == "spreadsheet" ||
        payload.document.typeId == "file"
      ) {

        let preparingToast = null;
        function addListeners(xhr) {
          xhr.addEventListener('loadstart', () => {
            // one toast for the download, also when it is asked again with a renewed token (SS05651)
            preparingToast = preparingToast || vm.$toasted.info("Preparing download");
          });
        }
        // SS06238: a failed download says so instead of stopping at "Preparing download"
        const downloadFailed = (detail) => {
          if (preparingToast && typeof preparingToast.goAway === "function") {
            preparingToast.goAway(0);
          }
          vm.$toasted.global.error({}, "Failed to download the document. Please try again.");
          console.error(detail);
        };

        documentModel.outputType = 4;
        // SS06238: GET as before; POST the model when the query string would be too long for IIS
        const documentRequest = buildDocumentRequest(PDF_DOCUMENT_PATH, documentModel);
        let url = documentRequest.url;
        /* SS05651: an expired access token is renewed and the download asked for again, as the shared
           HTTP client does for every other request; each attempt reads the token when it is sent. */
        const sendDownload = (answer) => {
          var xhr = new XMLHttpRequest();
          addListeners(xhr);
          xhr.open(
            documentRequest.method,
            webAppConfig.baseURL ? webAppConfig.baseURL + url : url,
            true
          );
          var token = JSON.parse(localStorage.getItem("ls.authorizationData"))[
            "access_token"
          ];
          xhr.setRequestHeader(
            "Content-type",
            documentRequest.method === "POST"
              ? DOCUMENT_POST_CONTENT_TYPE
              : "application/x-www-form-urlencoded"
          );
          xhr.setRequestHeader("authorization", "bearer " + token);
          xhr.setRequestHeader("wsId", authorizationStore.getters.runAsWsId || this.workspaceId);
          xhr.responseType = "arraybuffer";
          xhr.onload = () => answer(xhr);
          // network failure, or a CORS-blocked response
          xhr.onerror = function () {
            downloadFailed(`Document download failed (${xhr.status || "network error"})`);
          };
          xhr.send(documentRequest.body);
        };
        sendWithTokenRefreshCallback(sendDownload, () => authorizationStore.dispatch("refreshToken"), (xhr) => {
          if (xhr.status === 200) {
            var filename = "";
            var disposition = xhr.getResponseHeader("Content-Disposition");
            if (disposition && disposition.indexOf("attachment") !== -1) {
              var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              var matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1])
                filename = matches[1].replace(/['"]/g, "");
            }
            var type = xhr.getResponseHeader("Content-Type");

            var blob;
            if (typeof File === "function") {
              try {
                blob = new File([xhr.response], filename, { type: type });
              } catch (e) {
                /* Edge */
              }
            }
            if (typeof blob === "undefined") {
              blob = new Blob([xhr.response], { type: type });
            }

            if (typeof window.navigator.msSaveBlob !== "undefined") {
              /* IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed." */
              window.navigator.msSaveBlob(blob, filename);
            } else {
              var URL = window.URL || window.webkitURL;
              var downloadUrl = URL.createObjectURL(blob);

              if (filename) {
                /* use HTML5 a[download] attribute to specify filename */
                var a = document.createElement("a");
                /* safari doesn't support this yet */
                if (typeof a.download === "undefined") {
                  window.location = downloadUrl;
                } else {
                  a.href = downloadUrl;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                }
              } else {
                window.location = downloadUrl;
              }
              /* cleanup */
              setTimeout(function () {
                URL.revokeObjectURL(downloadUrl);
              }, 100);
            }
          } else {
            downloadFailed(`${xhr.status} (${xhr.statusText})`);
          }
        });
      } else if (payload.document.typeId == "pdf") {
        // SS06258: in the preview the entry's route names the open PDF, so the other device opens it too
        if (vm.builderMode) {
          const query = queryWithOpenDocument(vm.$route.query, payload, (id) => {
            const ids = commons.queryDecodeids(id);
            return ids && ids.entryId;
          });
          if (query) vm.$router.replace({ query }).catch(() => {});
        }
        vm.$refs.documentModal.show({
          request: buildDocumentRequest(PDF_DOCUMENT_PATH, documentModel),
          parameters: {
            moduleId: payload.moduleId,
            templateId: payload.document.id,
            template: payload.document.template,
            entryId: payload.items && payload.items.length ? payload.items[0].id : null,
            entryIds: payload.items && payload.items.length
            ? payload.items.map((x) => x.id)
            : null,
            listingId: payload.listingId,
            filters: payload.filters,
            inputs: payload.inputs,
          }
        });
      } else {
        const encodedUri = prefixHTMLUrl + JSON.stringify(documentModel);
        window.open(`/#/htmlViewer?file=${encodeURIComponent(encodedUri)}`, "_blank");
      }
    };
    window.eventBus.$on("showPDFDocument", this._onShowPDFDocument);
    // Settings (alpha): a section that has not moved out of this dialog yet opens the
    // dialog on its tab, so nothing is lost while the area is built section by section.
    this._onSettingsOpenLegacy = (evt) => {
      const section = (evt && evt.section) || "workspace";
      this.selectWorkspaceOption("edit", { section, legacy: true });
    };
    window.eventBus.$on("settings:openLegacy", this._onSettingsOpenLegacy);
    // Settings: the workspace on screen was deleted there, or the person left it.
    this._onSettingsWorkspaceGone = (evt) => {
      this.moveOffWorkspace((evt && evt.id) || this.workspaceId);
    };
    window.eventBus.$on("settings:workspaceGone", this._onSettingsWorkspaceGone);
    // Analytic Bench: it lives in the Dashboard page, so opening a report is a ROUTE, not a panel
    // (user ruling 2026-08-23). The chat's report card and any other caller land on
    // Dashboard › My Insights with the insight already open.
    this._onOpenAnalytic = (evt) => {
      const id = evt && evt.id ? String(evt.id) : "";
      const query = id ? { tab: "insights", report: id } : { tab: "insights" };
      this.$router.push({ name: "dashboard", query }).catch(() => { /* already there: the page watches the query */ });
    };
    window.eventBus.$on("gney:openReport", this._onOpenAnalytic);
    window.eventBus.$on("gney:openBench", this._onOpenAnalytic);
    // A PUBLISHED report (P2 promotion) lives on the Reports tab: same route, ?analytic= instead.
    this._onOpenPublished = (evt) => {
      const id = evt && evt.id ? String(evt.id) : "";
      const query = id ? { tab: "reports", analytic: id } : { tab: "reports" };
      this.$router.push({ name: "dashboard", query }).catch(() => { /* already there */ });
    };
    window.eventBus.$on("gney:openAnalytic", this._onOpenPublished);
    // signalr:refreshNotification
    this._onRefreshNotification = async () => {
      this.loadNotificationCount();
    };
    window.eventBus.$on("refreshNotification", this._onRefreshNotification);
    // signalr:selectionInvalidated — drop cached dropdown option lists sourced from the written
    // module (docs/fe-caching-and-formula-performance-design.md §5).
    this._onSelectionInvalidated = (payload) => {
      if (payload && payload.moduleId != null) {
        selectionCache.invalidateModule(payload.moduleId);
      }
    };
    window.eventBus.$on("signalr.selectionInvalidated", this._onSelectionInvalidated);
    // Reconnect gap: updates during a SignalR drop are missed, so clear the whole selection cache
    // once the connection is re-established (cheap; guarantees consistency after any gap). The
    // initial connect doesn't clear — the flag is only armed by a prior disconnect/reconnect.
    // Use a named handler ref so the $off below doesn't nuke other components' connectionState
    // listeners (e.g. FTSDebugPanel).
    this._onSelectionConnectionState = (state) => {
      if (state === "disconnected" || state === "reconnecting") {
        this._signalrWasDisconnected = true;
      } else if (state === "connected" && this._signalrWasDisconnected) {
        this._signalrWasDisconnected = false;
        selectionCache.clearAll();
      }
    };
    window.eventBus.$on("signalr.connectionState", this._onSelectionConnectionState);
    // form skip confirmation
    this._onFormSkipConfirmation = (payload) => {
      this.$refs.ftsFormSkipConfirmation.addQueue(payload);
    };
    window.eventBus.$on("formSkipConfirmation", this._onFormSkipConfirmation);
    // load workspace
    let hasCache = false;
    // SS05783: a deep link may belong to another workspace, and releasing contentReady mounts
    // the router-view — the dashboard fires quickViewData on mount, which would request the
    // entry under the cached workspace. Keep the shortcut only while the cached workspace
    // already carries the linked module; otherwise wait for the module-keyed bootstrap below (SS05685/SS05686).
    const deepLinkModuleId = this.deepLinkModuleId();
    const queryDemoId = this.$route.query && this.$route.query.demoId ? this.$route.query.demoId : null;
    if (!queryDemoId) {
      hasCache = await this.loadWorkspaceCache(workspaceId);
      console.log("[Mounted]:", this.isLiteMode);
      if (hasCache && !this.isLiteMode
        && (!deepLinkModuleId || this.workspaceHasModule(deepLinkModuleId))) {
        this.contentReady = true;
      }
    }

    /* SS05685/SS05686: an entry link names the module but never its workspace, so bootstrapping
       from the cached workspace sent the form request out under the wrong wsId. Resolve the
       owning workspace from the module instead.

       Deliberately keyed on ?id= alone, not on :moduleId. Both emailed shapes carry ?id= (the
       listing variant is /#/list/:moduleId?l=…&id=…), so this covers the reported links, while
       ordinary list navigation — which has :moduleId but no ?id= — keeps its existing cached
       bootstrap. Keying on :moduleId would have re-pointed every list page load at the module's
       OWNER workspace, which differs from the active one for a module shared across workspaces.

       Entry-only ids (`moduleUnspecified`, SS05672) carry no module and fall through unchanged. */
    let linkModuleId = null;
    if (!queryDemoId && this.$route.query && this.$route.query.id) {
      linkModuleId = commons.queryDecodeids(this.$route.query.id).moduleId || null;
    }

    // Whether the load below was superseded by another workspace request (a list view fetching the
    // workspace for a module the cache does not have aborts it). Its modules are then not yet known.
    let initialLoad = null;
    try {
      const preserveMenuState = !!clientStore.getters.workspaceId;
      /* A module that resolves to no reachable workspace needs no special handling here:
         loadWorkspace already retries once with no payload, which falls back to the user's
         default workspace, and the entry view then surfaces its own error. */
      initialLoad = await this.loadWorkspace(
        linkModuleId
          ? { moduleId: linkModuleId, preserveMenuState: preserveMenuState }
          : { id: workspaceId, demoId: queryDemoId, preserveMenuState: preserveMenuState },
        { isInitial: !hasCache }
      );
    } catch(err) {
      if (err && err.status === 404) {
        this.$router.replace({ name: 'setup-workspace' });
        throw new Error('No workspace found. Redirect to setup workspace');
      } else {
        authorizationStore.dispatch("logout");
        throw new Error(err);
      }
    }
    // load default workspace context
    if (this.workspace.hasContext && !this.isWorkspaceContext) {
      await this.setDefaultWorkspaceContext();
    }
    // back from Stripe Checkout → open the Plan & Credits tab (not awaited: a modal, not a step)
    this.openBillingReturn();
    // onboarding:redirect to list view
    let clientOnboarding = false;
    if (this.$route.query.redirect === 'list') {
      clientOnboarding = true;
      try {
        let defaultModule = null;
        const getDefaultModule = (list) => {
          return list.find((x) => {
            if (x.type === 'group') {
              return Array.isArray(x.children) && getDefaultModule(x.children);
            } else {
              defaultModule = x;
              return true;
            }
          });
        }
        Array.isArray(clientStore.getters.workspaceMenus) && getDefaultModule(clientStore.getters.workspaceMenus);
        if (defaultModule) {
          const route = commons.clientListViewRoute({
            moduleId: defaultModule.id,
            listId: Array.isArray(defaultModule.children) && defaultModule.children[0] && defaultModule.children[0].id
          });
          this.$router.replace(route);
        }
      } catch(err) { console.error(err); }
    }
    // builder:this.builderActionMessage() => window.location.reload();
    if (this.builderMode && this.$route.query.hasOwnProperty('runas')) {
      this.setRunAs({
        target: !!this.$route.query.runas ? this.$route.query.runas : null,
        loading: (val) => this.workspaceLoading = val,
        error: (val) => this.$toasted.global.error({ message: 'Failed to run as.' + val })
      });

      this.$router.replace({
        query: {
          ...this.$route.query,
          runas: undefined
        }
      });
    }
    // content ready
    if (this.contentReady != true) {
      this.contentReady = true;
    }
    // onboarding:tour
    if (clientOnboarding) {
      $cookies.set("client_onboarding", 1, "1y");
      this.$nextTick(() => {
        this.openModuleOnboardModal();
      });
    } else if ($cookies.get('client_onboarding') && $cookies.get('client_onboarding') == 1) {
      this.$nextTick(() => {
        this.startTour();
      });
    }
    // real-time change
    if (!this.builderMode) {
      const getRTCTimeout = () => (10 + commons.randomInt(0, 50)) * 1000;
      // signalr:workspaceChanged
      this._onWorkspaceChanged = async (payload) => {
        const { workspaceId, moduleId } = payload;

        // A module DESIGN save can restyle cached dropdown option text without any entry write, so
        // the `selectionInvalidated` push (entry writes only) never fires for it — e.g. changing a
        // connection field's Display Information (SS05926). Drop those records here, before the
        // debounced reload below and regardless of which workspace is on screen: the cache is keyed
        // by module, not by the workspace currently being viewed.
        if (moduleId != null) {
          selectionCache.invalidateDesign(moduleId);
        }

        this.setWorkspaceVersionInterval();

        if (this.workspaceId == workspaceId) {
          const originalWorkspaceId = workspaceId;

          if (this.workspaceChangedTimeout) {
            clearTimeout(this.workspaceChangedTimeout);
          }

          this.workspaceChangedTimeout = setTimeout(async () => {
            if (this.workspaceId != originalWorkspaceId) {
              this.workspaceRTCStatus = "abort";
              return;
            }

            this.workspaceRTCStatus = "initial";

            if (!navigator.onLine) {
              this.workspaceRTCStatus = "offline";
              return;
            }

            try {
              this.workspaceRTCStatus = "in-progress";
              const result = await this.loadWorkspace({
                id: this.workspaceId,
                forceReload: true,
                preserveMenuState: true,
                background: true
              });
              if (result && result.stale) {
                this.workspaceRTCStatus = "abort";
                return;
              }

              if (this.moduleId == moduleId) {
                window.eventBus.$emit("listing:schemaUpdated");
              }

              this.workspaceRTCStatus = "completed";
            } catch(err) {
              console.error(err); // to-do error
              this.workspaceRTCStatus = "error";
            }
          }, getRTCTimeout());
        } else {
          this.workspaceRTCStatus = "mismatch";
        }
      };
      window.eventBus.$on("signalr.workspaceChanged", this._onWorkspaceChanged);
      // signalr:userChanged
      this._onUserChanged = async (payload) => {
        const { workspaceId, user } = payload;

        this.setWorkspaceVersionInterval();

        if (!navigator.onLine) {
          this.userRTCStatus = "offline";
        }

        if (navigator.onLine && this.workspaceId == workspaceId) {
          const currentUser = authorizationStore.getters.isRunAs ? authorizationStore.state.runAs.target : authorizationStore.getters.userName;

          if (currentUser == user) {
            if (this.currentUserChangedTimeout) {
              clearTimeout(this.currentUserChangedTimeout);
            }

            this.currentUserChangedTimeout = setTimeout(async() => {
              this.userRTCStatus = "initial";
              await authorizationStore.dispatch("refreshToken");
              await authorizationStore.dispatch("profile");
              try {
                this.userRTCStatus = "in-progress";
                const result = await this.loadWorkspace({
                  id: this.workspaceId,
                  forceReload: true,
                  preserveMenuState: true,
                  background: true
                });
                if (result && result.stale) {
                  this.userRTCStatus = "abort";
                  return;
                }
                this.userRTCStatus = "completed";
              } catch(err) {
                console.error(err); // to-do error
                this.userRTCStatus = "error";
              }
            }, getRTCTimeout());
          } else {
            if (this.userChangedTimeout) {
              clearTimeout(this.userChangedTimeout);
            }

            this.userChangedTimeout = setTimeout(async() => {
              this.userRTCStatus = "initial";
              try {
                this.userRTCStatus = "in-progress"
                const users = await clientStore.dispatch("loadWorkspaceUsers", { id: this.workspaceId });
                const findUser = users.find(x => x.username == user);

                if (findUser) {
                  clientStore.dispatch("setWorkspaceUser", findUser);
                } else {
                  clientStore.dispatch("removeWorkspaceUser", user);
                }

                this.userRTCStatus = "completed";
              } catch(err) {
                console.error(err); // to-do error
                this.userRTCStatus = "error";
              }
            }, getRTCTimeout());
          }
        } else {
          if (this.currentUserChangedTimeout) {
            clearTimeout(this.currentUserChangedTimeout);
          }

          if (this.userChangedTimeout) {
            clearTimeout(this.userChangedTimeout);
          }
        }
      };
      window.eventBus.$on("signalr.userChanged", this._onUserChanged);

      // Only on a load that finished: after a superseded one, "no modules" is the stale cache
      // talking, and the dialog would open over a module the page is already showing.
      const loadSuperseded = Boolean(initialLoad && initialLoad.stale);
      if (!loadSuperseded && this.shouldPromptFirstModule()) {
        this.$nextTick(() => {
          this.openModuleDesignModal();
        });
      }

      this.setWorkspaceVersionInterval();
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
    // gney ai enabled
    const val = localStorage.getItem('gney-ai-enabled')
    this.enabledGneyAi = val === 'true'
  },
  beforeDestroy() {
    // remove EventListener
    if (this.builderMode) {
      window.removeEventListener("message", this.builderActionMessage);
    } else {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
    // reset run as
    if (!!this.isRunAs) {
      authorizationStore.dispatch("setRunAs");
    }
    // reset client state
    clientStore.dispatch("resetState");
    // reset theme
    document.body.classList.remove("dark");
    // remove prefers-color-scheme listener
    if (this._darkModeMql && this._onColorSchemeChange) {
      this._darkModeMql.removeEventListener('change', this._onColorSchemeChange);
      this._darkModeMql = null;
      this._onColorSchemeChange = null;
    }
    // clear interval
    if (this.workspaceVersionInterval) {
      clearInterval(this.workspaceVersionInterval);
      this.workspaceVersionInterval = null;
    }
    // clear timeout
    clearTimeout(this.workspaceChangedTimeout);
    clearTimeout(this.userChangedTimeout);
    clearTimeout(this.currentUserChangedTimeout);
    // off eventBus — pass the handler refs so we only remove our own listeners and don't
    // nuke co-listeners (e.g. Lite.vue / FTSDebugPanel) bound to the same events.
    window.eventBus.$off("signalr.workspaceChanged", this._onWorkspaceChanged);
    window.eventBus.$off("signalr.userChanged", this._onUserChanged);
    window.eventBus.$off("refreshNotification", this._onRefreshNotification);
    window.eventBus.$off("signalr.selectionInvalidated", this._onSelectionInvalidated);
    window.eventBus.$off("signalr.connectionState", this._onSelectionConnectionState);
    window.eventBus.$off("showPDFDocument", this._onShowPDFDocument);
    window.eventBus.$off("settings:openLegacy", this._onSettingsOpenLegacy);
    window.eventBus.$off("settings:workspaceGone", this._onSettingsWorkspaceGone);
    window.eventBus.$off("gney:openReport", this._onOpenAnalytic);
    window.eventBus.$off("gney:openBench", this._onOpenAnalytic);
    window.eventBus.$off("gney:openAnalytic", this._onOpenPublished);
    window.eventBus.$off("formSkipConfirmation", this._onFormSkipConfirmation);
  },
  beforeRouteUpdate(to, from, next) {
    if (this.builderMode) {
      if (
        builderCallback !== false &&
        !to.query.accessToken &&
        !to.query.refresh
      ) {
        console.info('Client Post Event to Builder', to.fullPath)
        postEventMessage({ path: to.fullPath });
      } else if (to.query.refresh) {
        delete to.query.refresh;
      }
    }

    if (this.workspaceLobby && to.name !== "lobby") {
      next({ name: "lobby" });
    } else {
      next();
    }
  },
  beforeRouteEnter(to, from, next) {
    if (commons.builderMode) {
      postEventMessage({ onload: true });
    }
    next();
  }
};
</script>

<style lang="scss">
/* Shell polish — style only, namespaced under .fts-client--refined so the shared .fts-client
   styles (also used by Studio.vue / Builder.vue) stay untouched. Values follow the Inistate
   Figma design system: Rounded/4 for controls and tiles, Rounded/12 for menus/sections/cards,
   2px-equivalent solid focus borders (no halo), and the Shadow-page recipes translated to
   rgba blacks so dark mode and all color themes inherit. Motion, press feedback, hover chips,
   and focus-visible outlines are additive polish the design system doesn't govern. */
.fts-client.fts-client--refined {
  /* backdrop behind the content card; lands on the design-system bg-secondary (#f6f6f6)
     in the default light theme */
  background-color: var(--drawer-background);
  background-image: linear-gradient(rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.02));

  /* sidenav rail */
  .client-sidenav {
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(0, 0, 0, 0.04));
    box-shadow: inset -1px 0 0 rgba(0, 0, 0, 0.06);
    .client-sidenav-wrapper {
      border-radius: 0.5rem;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);
    }
    .sidenav-menu .sidenav-menu-wrapper {
      border-radius: 0.5rem;
      transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease,
        box-shadow 0.15s ease;
      &:active {
        transform: scale(0.94);
      }
      &.active {
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
      }
      .sidenav-icon {
        transition: color 0.15s ease;
      }
      .sidenav-workspace {
        border-radius: 0.5rem;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
    }
    :focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.65);
      outline-offset: 2px;
    }
    .client-user-profile .user-profile-avatar {
      border-radius: 50%;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
      transition: box-shadow 0.15s ease;
      &:hover {
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
      }
    }
  }
  /* hairline between the workspace switcher and the nav icons */
  .client-sidenav-body > .notif-dropdown {
    position: relative;
    margin-top: 0.6875rem;
    &::before {
      content: "";
      position: absolute;
      top: -0.375rem;
      left: 50%;
      width: 1.375rem;
      height: 1px;
      transform: translateX(-50%);
      background-color: var(--sidenav-border, rgba(255, 255, 255, 0.25));
    }
  }
  /* The pill widens with its text, so it hangs from its right edge: anchored on the left it
     grew outwards, past the 50px rail - "10" ended at 51.5px, "99+" at 58px - and the rail's
     scroll container answered with a horizontal scrollbar (SS06123). A single digit sits
     where it always did. */
  .notif-dropdown .notif-dropdown-toggle .notif-count {
    top: -0.4375rem;
    left: auto;
    right: -0.5625rem;
    height: 1.25rem;
    width: auto;
    min-width: 1.25rem;
    padding: 0 0.25rem;
    border-radius: 0.625rem;
    border: 2px solid var(--sidenav-background);
    box-shadow: none;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  /* drawer */
  .client-drawer {
    .client-drawer-header .client-dropdown .client-dropdown-toggle {
      border-radius: 0.5rem;
      transition: background-color 0.15s ease, color 0.15s ease;
    }
    .client-drawer-toggle {
      border-radius: 0.5rem;
      transition: background-color 0.15s ease, color 0.15s ease;
    }
    /* module search: collapsed to an icon in the header, field revealed on click */
    .client-drawer-header {
      .client-drawer-search-toggle-wrapper {
        margin-left: 0.5rem;
      }
      .client-drawer-toggle-wrapper {
        margin-left: 0.375rem;
      }
      .client-drawer-search-toggle {
        height: 1.25rem;
        width: 1.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
        border-radius: 0.5rem;
        color: var(--drawer);
        opacity: 0.7;
        cursor: pointer;
        transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
        > svg {
          cursor: pointer;
        }
        &:hover {
          opacity: 1;
          background-color: var(--drawer-secondary-background);
          color: var(--drawer-secondary);
        }
      }
      .client-drawer-search-toggle-wrapper--active .client-drawer-search-toggle {
        opacity: 1;
        background-color: var(--drawer-hover-background);
        color: var(--drawer-hover);
      }
    }
    .drawer-search {
      margin: 0.25rem 0.25rem 0.5rem;
    }
    .drawer-search-enter-active,
    .drawer-search-leave-active {
      transition: opacity 0.15s ease, transform 0.15s ease;
    }
    .drawer-search-enter,
    .drawer-search-leave-to {
      opacity: 0;
      transform: translateY(-0.25rem);
    }
    .fts-module .module-wrapper {
      border-radius: 0.5rem;
      transition: background-color 0.15s ease, color 0.15s ease;
      &.active .module-name {
        font-weight: 500;
      }
    }
    .drawer-action-wrapper .drawer-action {
      border-radius: 0.5rem;
      transition: background-color 0.15s ease, color 0.15s ease;
    }
    .module-wrapper:focus-visible,
    .drawer-action:focus-visible,
    .client-drawer-toggle:focus-visible {
      outline: 2px solid var(--drawer-active);
      outline-offset: -2px;
    }
    &.collapsed:not(.close) {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
    }
    .client-user-profile .user-profile-info {
      transition: background-color 0.15s ease;
      &:hover {
        background-color: var(--drawer-hover-background);
      }
    }
  }

  /* content pane — flush at the top; left divider + soft side depth against the drawer */
  > .client-content {
    background-color: var(--fts-background);
    border-left: 1px solid var(--fts-border);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04), 0 6px 16px -8px rgba(0, 0, 0, 0.07);
  }

  /* listing — sticky header */
  .fts-list-view {
    .list-header.scrolled {
      box-shadow: 0 1px 0 var(--fts-border), 0 3px 6px rgba(0, 0, 0, 0.05);
    }
    .list-nav .list-nav-title {
      font-weight: 500;
    }
    .list-nav .list-nav-r .list-nav-action {
      transition: color 0.15s ease;
      &:hover {
        color: var(--fts-text);
      }
    }
    /* control icon strip as hover chips */
    .list-view-control .list-view-control-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.3125rem;
      border-radius: 0.5rem;
      transition: background-color 0.15s ease, color 0.15s ease;
      &:not(:first-child) {
        margin-left: 0.375rem;
      }
      &:last-child {
        margin-right: -0.3125rem;
      }
      &:not(.list-view-control-icon--loading):hover {
        background-color: rgba(var(--fts-gray-100-rgb), 0.9);
        color: var(--fts-text);
      }
      &.list-view-control-icon--active,
      &.list-view-control-icon--active:hover {
        color: var(--fts-primary);
      }
    }
  }

  /* listing — table */
  .fts-table-view.fts-table {
    > thead > tr > th {
      font-size: 0.8125rem;
      font-weight: 500;
    }
    .table-view-th .table-view-th-col {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
    > tbody > tr {
      transition: background-color 0.12s ease;
      > td:not(.sub-row) {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }
    }
    .table-view-shortcut {
      display: block;
      visibility: hidden;
      opacity: 0;
      transform: translateY(1px);
      pointer-events: none;
      transition: opacity 0.12s ease, transform 0.12s ease, visibility 0.12s;
      border-radius: 0.5rem;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.07), 0 1px 2px -2px rgba(0, 0, 0, 0.12);
      > .fts-btn {
        border-radius: inherit;
      }
    }
    tbody:hover .table-view-shortcut {
      visibility: visible;
      opacity: 1;
      transform: none;
      pointer-events: auto;
    }
    .fts-audit .audit-profile .profile-photo {
      box-shadow: 0 0 0 1px var(--fts-border);
    }
  }

  /* buttons — DS text treatment (sentence case + normal tracking): the base .fts-btn defaults
     to UPPERCASE + wide letter-spacing unless a call site opts out via text-transform-init, so
     standard Cancel/Submit/Save render uppercase next to sentence-case activity buttons. This
     conforms every client button to the Figma spec (Roboto Medium, tracking 0). */
  .fts-btn {
    text-transform: none;
    letter-spacing: normal;
  }
  /* buttons — 8px radius (DS control radius bumped 4→8), motion, keyboard focus */
  .fts-btn:not(.fts-btn-icon):not(.fts-btn-rounded) {
    border-radius: 0.5rem;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease,
      box-shadow 0.15s ease;
    &:focus-visible {
      outline: 2px solid var(--fts-primary);
      outline-offset: 2px;
    }
  }

  /* forms — input spec: radius 8 (DS control radius bumped 4→8), focus/error = 2px solid border, no halo */
  .fts-form-control {
    border-radius: 0.5rem;
    &:hover:not(:focus):not(:disabled):not(.disabled) {
      border-color: rgba(var(--fts-gray-400-rgb), 0.7);
    }
    &:focus {
      border-color: var(--fts-primary);
      box-shadow: inset 0 0 0 1px var(--fts-primary);
    }
    &.form-control--error:focus {
      border-color: var(--fts-danger);
      box-shadow: inset 0 0 0 1px var(--fts-danger);
    }
  }
  /* inline/compact grids stay flush like the base design */
  .compact-form-design .fts-form-control {
    border-radius: 0;
  }
  .v-select .dropdown-toggle {
    border-radius: 0.5rem;
  }
  .fts-form-container .fts-form-section.form-section-hoverable {
    border-radius: 0.75rem;
  }
  .fts-form-container
    .fts-form-section.form-section-tab
    .form-section-header
    .form-section-header-wrapper
    .form-section-header-title {
    border-radius: 0.5rem;
  }
}
/* dropdown elevation — the workspace menu renders in-tree; the notification menu is
   body-appended, so it carries its own refined class from the template */
.fts-client.fts-client--refined .client-content.client-dropdown-menu,
.notification-content.notification-dropdown-menu.notification-dropdown-menu--refined {
  border-radius: 0.75rem;
  border: 1px solid var(--fts-border);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
}
.fts-client.fts-client--refined .client-dropdown-menu .dropdown-option {
  border-radius: 0.5rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.notification-dropdown-menu--refined .notification-dropdown__content .notification-content {
  border-radius: 0.5rem;
  transition: background-color 0.15s ease;
}
/* integrations (alpha) flyout — body-appended, so it carries its own classes
   instead of inheriting the .fts-client--refined namespace */
.integrations-content.integrations-dropdown-menu {
  width: 17.5rem;
  max-width: 17.5rem;
  padding: 0;
  border-radius: 0.75rem;
  border: 1px solid var(--fts-border);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
  .integrations-dropdown__header {
    display: flex;
    align-items: center;
    padding: 0.75rem 0.75rem 0.375rem;
    .integrations-header-title {
      margin-right: 0.375rem;
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }
  .integrations-dropdown__content {
    padding: 0 0.25rem 0.25rem;
  }
  .integrations-option {
    display: flex;
    align-items: flex-start;
    padding: 0.375rem 0.5rem;
    border-radius: 0.5rem;
    color: var(--fts-text);
    text-decoration: none;
    transition: background-color 0.15s ease;
    &:hover {
      background-color: rgba(var(--fts-gray-100-rgb), 0.9);
    }
    &.router-link-active {
      background-color: rgba(var(--fts-primary-rgb), 0.1);
      .integrations-option-icon,
      .integrations-option-title {
        color: var(--fts-primary);
      }
    }
    .integrations-option-icon {
      margin-top: 0.1875rem;
      margin-right: 0.5rem;
      color: var(--fts-default);
    }
    .integrations-option-title {
      font-weight: 500;
      line-height: 1.25rem;
    }
    .integrations-option-description {
      color: var(--fts-default);
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }
}

/* --- Client pages, calm density: the shared chrome, defined once
   (docs/frontend-dashboard-calm-density-proposal.md S1 + frontend-calendar-calm-density-proposal.md C1).
   .fts-client-header is rendered by Dashboard, Calendar and Lobby and by nothing else, so
   the title line, the ghost icon button and the page tokens belong to the shell rather
   than to each page's own block; the pages keep only what is theirs. Studio and Builder
   carry .fts-client without --refined, so none of this reaches them. */
.fts-client.fts-client--refined {
  --cd-hairline: var(--fts-gray-200);
  --cd-rule: var(--fts-gray-100);
  --cd-ink-2: var(--fts-gray-500);
  --cd-hover: var(--fts-gray-50);
  /* the wash that over-paints an inline fill - a state chip on the dashboard, an event on
     the calendar - so the configured hue survives as a tint instead of a block of colour */
  --cd-wash: rgba(252, 252, 252, 0.86);

  /* the title line: 51px (a 35px button row inside 8px of padding) becomes 40px */
  .fts-client-header {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
    .client-header-label {
      line-height: 1.75rem;
      .client-header-label-icon {
        display: none;
      }
      .client-header-label-title {
        font-weight: 600;
        font-size: 0.9375rem;
      }
    }
    .client-header__r .client-header-action:not(:first-child) {
      margin-left: 0.25rem;
    }
    .fts-btn.fts-btn-v2.fts-btn-icon {
      width: 1.75rem;
      min-width: 1.75rem;
      max-width: 1.75rem;
      height: 1.75rem;
      max-height: 1.75rem;
      border-radius: 0.25rem;
      font-size: 0.8125rem;
      color: var(--fts-default);
      &:hover {
        background-color: var(--fts-gray-100);
        color: var(--fts-text);
      }
      /* an "on" state is weight and ground, not the brand colour */
      &.fts-btn-primary {
        background-color: var(--fts-gray-100);
        color: var(--fts-text);
      }
    }
    .client-header-save {
      font-size: 0.75rem;
      color: var(--cd-ink-2);
    }
  }
}
body.dark .fts-client.fts-client--refined {
  --cd-wash: rgba(33, 33, 33, 0.82);
}
</style>
